import { FC } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useMyAccountHasFeature } from '@/api/queries/me/useGetMyAccountFeatures';
import { PaymentScheduleStage } from './PaymentScheduleStage';
import {
  PaymentScheduleFormValues,
  type ProposalStageCardProps,
} from './types';
import { useStagePercentageField } from './useStagePercentageField';

export const ProposalInstalmentStageCard: FC<ProposalStageCardProps> = (
  props
) => {
  const {
    index,
    proposalTotal,
    amount,
    amountCoveredByDeposit,
    remainingPercentage,
    onDepositChange,
    onRemove,
  } = props;

  const percentage = useStagePercentageField(index, 'instalment');

  return (
    <PaymentScheduleStage.CardShell
      index={index}
      isInvalid={remainingPercentage < 0}
      onRemove={onRemove}
      actions={
        <>
          {onDepositChange && (
            <PaymentScheduleStage.DepositCheckbox
              index={index}
              isDeposit={false}
              onChange={onDepositChange}
            />
          )}
          <PaymentScheduleStage.AdjustPercentageButton
            percentage={percentage}
            remainingPercentage={remainingPercentage}
          />
        </>
      }
    >
      <PaymentScheduleStage.DescriptionField index={index} />
      <PaymentScheduleStage.PercentageAndAmountFields
        index={index}
        percentage={percentage}
        proposalTotal={proposalTotal}
        amount={amount}
      />
      {!!amountCoveredByDeposit && (
        <PaymentScheduleStage.DepositCoverNote
          amount={amount}
          amountCoveredByDeposit={amountCoveredByDeposit}
        />
      )}
    </PaymentScheduleStage.CardShell>
  );
};

export const ProposalDepositStageCard: FC<ProposalStageCardProps> = (props) => {
  const { index } = props;
  const { control } = useFormContext<PaymentScheduleFormValues>();
  const basisType = useWatch({
    control,
    name: `paymentSchedule.stages.${index}.basis.type`,
  });

  return basisType === 'fixed' ? (
    <FixedDepositStageCard {...props} />
  ) : (
    <PercentageDepositStageCard {...props} />
  );
};

const PercentageDepositStageCard: FC<ProposalStageCardProps> = (props) => {
  const {
    index,
    proposalTotal,
    amount,
    remainingPercentage,
    onDepositChange,
    onRemove,
  } = props;
  const isFixedDepositEnabled = useMyAccountHasFeature('hasFixedDeposits');

  const percentage = useStagePercentageField(index, 'deposit');

  return (
    <PaymentScheduleStage.CardShell
      index={index}
      isInvalid={remainingPercentage < 0}
      onRemove={onRemove}
      actions={
        <>
          {onDepositChange && (
            <PaymentScheduleStage.DepositCheckbox
              index={index}
              isDeposit
              onChange={onDepositChange}
            />
          )}
          <PaymentScheduleStage.AdjustPercentageButton
            percentage={percentage}
            remainingPercentage={remainingPercentage}
          />
        </>
      }
    >
      {isFixedDepositEnabled && (
        <PaymentScheduleStage.DepositTypeField
          index={index}
          total={proposalTotal}
        />
      )}
      <PaymentScheduleStage.DescriptionField index={index} />
      <PaymentScheduleStage.PercentageAndAmountFields
        index={index}
        percentage={percentage}
        proposalTotal={proposalTotal}
        amount={amount}
      />
    </PaymentScheduleStage.CardShell>
  );
};

const FixedDepositStageCard: FC<ProposalStageCardProps> = (props) => {
  const {
    index,
    proposalTotal,
    remainingPercentage,
    onDepositChange,
    onRemove,
  } = props;
  const isFixedDepositEnabled = useMyAccountHasFeature('hasFixedDeposits');

  return (
    <PaymentScheduleStage.CardShell
      index={index}
      isInvalid={remainingPercentage < 0}
      onRemove={onRemove}
      actions={
        onDepositChange && (
          <PaymentScheduleStage.DepositCheckbox
            index={index}
            isDeposit
            onChange={onDepositChange}
          />
        )
      }
    >
      {isFixedDepositEnabled && (
        <PaymentScheduleStage.DepositTypeField
          index={index}
          total={proposalTotal}
        />
      )}
      <PaymentScheduleStage.DescriptionField index={index} />
      <PaymentScheduleStage.FixedDepositFields index={index} />
    </PaymentScheduleStage.CardShell>
  );
};
